import { Component, computed, input, signal } from '@angular/core';
import { delay } from '@gametailors/web-core/misc';
import { AudioEffectPlayer, AudioPlayer } from '../../utils/audio.utils';
import { RidDiceFace } from '../rid-dice-face/rid-dice-face';

/** duration of the roll animation in ms */
const ROLL_DURATION = 3000;
const PRESENT_DURATION = 400;
const ROLL_BEAT_TIMES_MS = [32, 128, 224, 320, 416, 512, 608, 752, 848, 944, 1136, 1232, 1408, 1888]; // used for the audio effect

const FACE_ROTATIONS = [
  { x: 0, y: 0 },
  { x: 360 - 90, y: 0 },
  { x: 0, y: 90 },
  { x: 0, y: 360 - 90 },
  { x: 90, y: 0 },
  { x: 0, y: 180 },
];

@Component({
  selector: 'rid-dice',
  imports: [RidDiceFace],
  templateUrl: './rid-dice.html',
  styleUrl: './rid-dice.css',
  host: {
    '[style.--dice-color]': 'cssColor()',
  },
})
export class RidDice {
  /** Color of the dice (Tailwind color name, e.g. 'blue-500', 'red-400') */
  color = input<string | undefined>(undefined);

  target = signal<{ x: number; y: number }>({ x: 0, y: 0 });
  isPresenting = signal(false);
  protected cssColor = computed(() => {
    const c = this.color();
    return c ? `var(--color-${c})` : undefined;
  });

  /**
   * Rolls the dice with animation.
   * @param targetFace Optional face to land on (0-5). If not provided, rolls to a random face.
   * @returns Promise that resolves with the final face value when the animation completes.
   */
  async roll(targetFace: number): Promise<void> {
    const nextX = Math.floor(this.target().x / 360) * 360 + 3 * 360 + FACE_ROTATIONS[targetFace].x;
    const nextY = Math.floor(this.target().y / 360) * 360 + 3 * 360 + FACE_ROTATIONS[targetFace].y;

    this.target.set({ x: nextX, y: nextY });

    void this.playRollAudio();
    await delay(ROLL_DURATION);
  }

  /**
   * Presents the dice by scaling up slightly.
   * @returns Promise that resolves when the animation completes.
   */
  async present(): Promise<void> {
    this.isPresenting.set(true);
    await delay(PRESENT_DURATION);
  }

  private async playRollAudio(): Promise<void> {
    void AudioEffectPlayer.play('SHORT_SLIDE');

    let previousBeatTimeMs = 0;

    for (const beatTimeMs of ROLL_BEAT_TIMES_MS) {
      await delay(beatTimeMs - previousBeatTimeMs);

      previousBeatTimeMs = beatTimeMs;
      const beatProgress = beatTimeMs / ROLL_DURATION;
      const volume = Math.max(0.18, 0.34 - beatProgress * 0.1);
      const playbackRate = 1.04 - beatProgress * 0.12;

      void AudioPlayer.play('/audio/dice-beat.wav', { volume, playbackRate });
    }
  }
}
